import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Button, TextInput, Badge } from '@mantine/core';
import { IconPlus, IconPackages } from '@tabler/icons-react';
import PageHeader from '@/components/page-header';
import CardModal from '@/components/card-modal';
import { SkeletonList } from '@/components/snippets';
import { setWebTitle } from '@/lib/format-utils';
import { useProducts, useCreateProduct } from '@/hooks/api-hooks';
import { PRODUCT_PATH } from '@/lib/navigation';
import { Product } from '@turizi/shared';
import { ServiceTitleStatic } from '@/components/services/service-title';
import { TagIcon } from '@/lib/tag-utils';

export default function ProductsPage(): JSX.Element {
    const { t } = useTranslation();
    setWebTitle(t('products'));

    const [createModalOpened, setCreateModalOpened] = useState(false);
    const [nameDraft, setNameDraft] = useState('');

    const { data, isLoading } = useProducts();

    const products: Product[] = data?.data || [];

    return (
        <div>
            <PageHeader
                title={t('products')}
                rightSection={
                    <Button
                        leftSection={<IconPlus size={16} />}
                        color="black"
                        size="xs"
                        radius="xl"
                        onClick={() => setCreateModalOpened(true)}
                    >
                        {t('create_product')}
                    </Button>
                }
            />

            <div className="panel-content">
                {isLoading ? (
                    <SkeletonList />
                ) : products.length === 0 ? (
                    <div className="p-12 text-center border border-dashed border-gray-300 rounded-lg bg-gray-50/50">
                        <IconPackages size={48} className="mx-auto text-gray-400 mb-3" stroke={1} />
                        <div className="font-semibold text-gray-700 text-base mb-1">{t('no_products_yet')}</div>
                        <div className="text-gray-500 text-xs mb-4">{t('products_description')}</div>
                        <Button
                            leftSection={<IconPlus size={16} />}
                            color="black"
                            size="xs"
                            radius="xl"
                            onClick={() => setCreateModalOpened(true)}
                        >
                            {t('create_product')}
                        </Button>
                    </div>
                ) : (
                    <div className="solid-box-border overflow-hidden p-0">
                        <div className="bg-slate-50 border-b border-slate-200 px-4 py-2.5 flex items-center gap-4 text-xs uppercase font-bold text-gray-600">
                            <div className="w-1/3 min-w-[200px]">{t('product_name')}</div>
                            <div className="flex-1">{t('linked_services')}</div>
                        </div>
                        <div className="divide-y divide-slate-100">
                            {products.map((product) => (
                                <Link
                                    key={product.id}
                                    to={PRODUCT_PATH.replace(':id', String(product.id))}
                                    className="flex items-start gap-4 px-4 py-3 hover:bg-slate-50/80 transition-colors cursor-pointer text-inherit no-underline block"
                                >
                                    <div className="w-1/3 min-w-[200px] font-bold text-sm text-slate-900">
                                        {product.name}
                                    </div>
                                    <div className="flex-1 space-y-0.5">
                                        {product.services && product.services.length > 0 ? (
                                            product.services.map((service) => (
                                                <div
                                                    key={service.id}
                                                    className="p-0.5 rounded bg-gray-50 flex items-center gap-1 text-xs text-black"
                                                >
                                                    <TagIcon name={service.category} hideText />
                                                    {ServiceTitleStatic(service)}
                                                    <div className='text-slate-500 text-xxs'>#{service.id}</div>
                                                </div>
                                            ))
                                        ) : (
                                            <span className="text-xs text-gray-400 italic">{t('no_services_linked')}</span>
                                        )}
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
